import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useBranding, resolveAppDisplayName } from "../../context/BrandingContext";
import { getFooterStyles } from "../../theme/storeStyles";

/** Build a wa.me URL from a raw phone string + pre-filled message. */
function whatsappHref(raw, msg) {
  const digits = (raw || "").replace(/\D/g, "");
  if (!digits) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
}

/** About Us / Contact Us pages this store has published (GET /legal → `published`). */
function usePublishedPages() {
  const [published, setPublished] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/legal")
      .then(({ data }) => { if (alive) setPublished(data?.published || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return published;
}

export default function StoreFooter() {
  const { branding } = useBranding();
  const published = usePublishedPages();
  const brand = resolveAppDisplayName(branding);
  const styles = getFooterStyles(branding?.theme_preset);

  const contactHref = whatsappHref(
    branding?.contact_whatsapp,
    `Hi! I have a question about my order on ${brand}.`,
  );
  const careersHref = whatsappHref(
    branding?.careers_whatsapp,
    `Hi! I'd like to know about career opportunities at ${brand}.`,
  );
  return (
    <footer
      className={`${styles.root} hide-on-native`}
      data-testid="store-footer"
      data-theme-preset={branding?.theme_preset || "kalki"}
    >
      <div className="px-6 md:px-12 lg:px-24 py-16 grid md:grid-cols-4 gap-10">
        <div>
          <div className={styles.brandWrap}>{brand}</div>
          <p className={styles.brandText}>
            {branding?.tagline ||
              "Your own multi-vendor marketplace. Electronics, home, beauty and more — delivered fast across India."}
          </p>
        </div>
        <div>
          <div className={styles.colHeading}>Shop</div>
          <ul className="space-y-2 text-sm">
            <li><Link className={styles.linkClass} to="/shop/electronics">Electronics</Link></li>
            <li><Link className={styles.linkClass} to="/shop/mobiles">Mobiles</Link></li>
            <li><Link className={styles.linkClass} to="/shop/home-kitchen">Home & Kitchen</Link></li>
            <li><Link className={styles.linkClass} to="/shop/beauty">Beauty</Link></li>
          </ul>
        </div>
        <div>
          <div className={styles.colHeading}>Company</div>
          <ul className="space-y-2 text-sm">
            {/* "Start your own store" promotes the SaaS — only the
                default tenant (the platform host) should advertise it.
                Whitelabel tenants shouldn't funnel their visitors to a
                competing storefront onboarding. */}
            {(branding?.tenant_slug || "default") === "default" && (
              <li>
                <Link
                  className={styles.linkClass}
                  to="/start-store"
                  data-testid="footer-start-store-link"
                >
                  Start your own store
                </Link>
              </li>
            )}
            {published.includes("about") && (
              <li>
                <Link className={styles.linkClass} to="/about" data-testid="footer-about-link">
                  About Us
                </Link>
              </li>
            )}
            <li>
              {published.includes("contact") ? (
                <Link className={styles.linkClass} to="/contact" data-testid="footer-contact-link">
                  Contact Us
                </Link>
              ) : contactHref ? (
                <a
                  className={styles.linkClass}
                  href={contactHref}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-contact-whatsapp"
                >
                  Contact
                </a>
              ) : (
                <span className={styles.linkClass}>Contact</span>
              )}
            </li>
            <li>
              {careersHref ? (
                <a
                  className={styles.linkClass}
                  href={careersHref}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-careers-whatsapp"
                >
                  Careers
                </a>
              ) : (
                <span className={styles.linkClass}>Careers</span>
              )}
            </li>
          </ul>
        </div>
        <div>
          <div className={styles.colHeading}>Stay in Touch</div>
          <p className={`${styles.brandText} mb-4`}>Get first dibs on new launches, deals and price drops.</p>
          <div className={styles.newsletterWrap}>
            {/* `min-w-0`: a flex child defaults to min-width:auto, so this
                input refused to shrink below its intrinsic width and pushed
                the Join button past the viewport — 18px of horizontal
                overflow on every storefront page at tablet widths. */}
            <input
              className="flex-1 min-w-0 bg-transparent py-2 text-sm focus:outline-none placeholder:opacity-60"
              placeholder="you@email.com"
              data-testid="newsletter-input"
            />
            <button
              className={`${styles.newsletterBtn} shrink-0 whitespace-nowrap`}
              data-testid="newsletter-submit"
            >
              Join →
            </button>
          </div>
        </div>
      </div>
      <div className={`px-6 md:px-12 lg:px-24 py-4 ${styles.divider} flex flex-col md:flex-row justify-between items-center gap-2`}>
        <div className={styles.microCopy}>© {new Date().getFullYear()} {brand} · Powered by GetOwnApp</div>
        <div className={`flex flex-wrap gap-x-6 gap-y-1 ${styles.microCopy}`}>
          <Link to="/legal/terms" className={styles.linkClass} data-testid="footer-terms-link">Terms</Link>
          <Link to="/legal/privacy" className={styles.linkClass} data-testid="footer-privacy-link">Privacy</Link>
          <Link to="/legal/shipping" className={styles.linkClass} data-testid="footer-shipping-link">Shipping</Link>
          <Link to="/legal/refund" className={styles.linkClass} data-testid="footer-refund-link">Refunds</Link>
        </div>
      </div>
      {/* Below lg the fixed MobileBottomNav (+ floating cart pill above it)
          overlays the page bottom; without this room the last row above sat
          underneath them. Inside the footer so it keeps the footer's
          background, and hidden with it in the native shell. */}
      <div className="h-28 lg:hidden" aria-hidden="true" />
    </footer>
  );
}
